/**
 * Utility functions for tracking file upload progress
 */

export interface UploadProgress {
  isUploading: boolean;
  currentFileIndex: number;
  totalFiles: number;
  currentFileName: string;
  currentFileProgress: number; // 0-100 for S3 upload only
  successCount: number;
  failedCount: number;
  cancelRequested: boolean;
}

export interface CancelToken {
  cancelled: boolean;
}

export interface UploadResult {
  success: boolean;
  fileName: string;
  error?: string;
}

/**
 * Uploads a file to S3 using XMLHttpRequest with progress tracking
 *
 * @param url - Presigned S3 URL
 * @param file - File to upload
 * @param bucketOwner - S3 bucket owner for header
 * @param onProgress - Callback for progress updates (0-100)
 * @param cancelToken - Optional cancellation token to abort upload
 * @returns Promise that resolves when upload completes
 */
export const uploadFileWithProgress = (
  url: string,
  file: File,
  bucketOwner: string,
  onProgress: (percentage: number) => void,
  cancelToken?: CancelToken
): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already cancelled before starting
    if (cancelToken?.cancelled) {
      reject(new Error("Upload cancelled"));
      return;
    }

    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        onProgress(percentage);
      }
    };

    // Handle successful completion
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    // Handle errors
    xhr.onerror = () => {
      reject(new Error("Upload failed due to network error"));
    };

    // Handle abort
    xhr.onabort = () => {
      reject(new Error("Upload cancelled"));
    };

    // Check for cancellation during upload
    if (cancelToken) {
      const checkCancellation = setInterval(() => {
        if (cancelToken.cancelled) {
          xhr.abort();
          clearInterval(checkCancellation);
        }
      }, 100);

      xhr.onloadend = () => {
        clearInterval(checkCancellation);
      };
    }

    // Open and send request
    xhr.open("PUT", url);
    xhr.setRequestHeader("x-amz-expected-bucket-owner", bucketOwner);
    xhr.send(file);
  });
};

/**
 * Creates initial upload progress state
 */
export const createInitialUploadProgress = (): UploadProgress => ({
  isUploading: false,
  currentFileIndex: 0,
  totalFiles: 0,
  currentFileName: "",
  currentFileProgress: 0,
  successCount: 0,
  failedCount: 0,
  cancelRequested: false,
});
